/**
 * The course symbol (Issue 574): a small identifier glyph beside the short name, drawn inline (no emoji, no icon font).
 * Decorative and `aria-hidden`: the short name always sits beside it, and it never encodes status.
 */

import type { CourseResponse } from '@davinci/contracts';

type Symbol_ = CourseResponse['symbol'];
type SymbolKey = Symbol_['key'];

/** Maths sub-keys are text glyphs; the die for probability is drawn. */
const TEXT_GLYPHS: Partial<Record<SymbolKey, string>> = {
  mathematics: '∑',
  calculus: '∫',
  'linear-algebra': '[ ]',
  discrete: '∀',
  algebra: 'x²',
  geometry: '△',
  'number-theory': 'ℤ',
};

/** 24x24 stroke paths, one per family. Kept small and simple. */
const PATHS: Partial<Record<SymbolKey, React.ReactNode>> = {
  medicine: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.6-7 10-7 10zM6 12h3l1.5-3 3 6 1.5-3h3" />,
  economics: <path d="M3 17l5-6 4 3 8-9M15 5h5v5" />,
  languages: <path d="M3 18l4-11 4 11M4.5 14h5M14 18l3-7 3 7M15 16h4" />,
  education: <path d="M2 9l10-5 10 5-10 5zM6 11.5V16c3 2.5 9 2.5 12 0v-4.5" />,
  sociology: <path d="M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 19c0-3 3-5 6-5s6 2 6 5M16 11a2.5 2.5 0 1 0 0-5M17 14c2.5.3 4 2 4 5" />,
  law: <path d="M12 4v16M7 20h10M4 8h16M4 8l-2 6a3 3 0 0 0 6 0zM20 8l-2 6a3 3 0 0 0 6 0z" />,
  history: <path d="M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 0 2 2H9a2 2 0 0 1-2-2zM7 4a2 2 0 0 0-2 2v2h2M10 9h6M10 13h6" />,
  politics: <path d="M3 10h18M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18M12 4l9 4H3z" />,
  'life-sciences': <path d="M8 3c0 6 8 6 8 12s-8 6-8 6M16 3c0 6-8 6-8 12M9 7h6M9 17h6" />,
  agriculture: <path d="M5 19c0-8 5-13 14-14 0 9-5 14-13 14M5 19c3-4 6-7 9-9" />,
  psychology: <path d="M12 20V9M9 6a3 3 0 0 1 6 0 3 3 0 0 1 3 4 3 3 0 0 1-2 5 3 3 0 0 1-4 3 3 3 0 0 1-4-3 3 3 0 0 1-2-5 3 3 0 0 1 3-4z" />,
  'area-studies': <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />,
  'computer-science': <path d="M9 7l-5 5 5 5M15 7l5 5-5 5M13.5 5l-3 14" />,
  chemistry: <path d="M10 3h4M11 3v6l-5 9a2 2 0 0 0 2 3h8a2 2 0 0 0 2-3l-5-9V3M8 15h8" />,
  philosophy: <path d="M5 6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-5l-4 4v-4H7a2 2 0 0 1-2-2zM10 9.5a2 2 0 1 1 3 1.7c-.7.4-1 .8-1 1.3" />,
  physics: <path d="M12 12m-1.5 0a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0M12 12c5-6 9-6 9-3s-4 6-9 3-9-3-9-6 4-3 9 3zM12 12c5 6 9 6 9 3" />,
  religion: <path d="M7 4h10a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM5 18a2 2 0 0 1 2-2h12M12 7v5M10 9h4" />,
  geography: <path d="M12 21s-6-6-6-11a6 6 0 0 1 12 0c0 5-6 11-6 11zM12 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />,
  earth: <path d="M2 19l7-12 4 7 3-4 6 9zM8 11l1.5 2 1.5-1.5" />,
  communication: <path d="M4 10v4h3l8 4V6L7 10zM19 9a4 4 0 0 1 0 6M7 14l1.5 5h2L10 15" />,
  arts: <path d="M12 3a9 9 0 0 0 0 18c1.5 0 2-1 1.5-2.2-.5-1.3.3-2.3 1.7-2.3H17a4 4 0 0 0 4-4c0-5-4-9.5-9-9.5zM7.5 11h.01M10 7.5h.01M14.5 7.5h.01" />,
  statistics: <path d="M3 19h18M4 19c3 0 3-14 8-14s5 14 8 14" />,
  probability: <path d="M5 5h14v14H5zM9 9h.01M15 9h.01M12 12h.01M9 15h.01M15 15h.01" />,
};

export function CourseSymbol({ symbol }: { readonly symbol: Symbol_ }): React.JSX.Element {
  const glyph = TEXT_GLYPHS[symbol.key];
  if (glyph !== undefined) {
    return (
      <span className="course-symbol course-symbol-glyph" aria-hidden="true" dir="ltr">
        {glyph}
      </span>
    );
  }
  if (symbol.key === 'letter') {
    return (
      <span className="course-symbol course-symbol-glyph" aria-hidden="true">
        {symbol.letter}
      </span>
    );
  }
  return (
    <svg
      className="course-symbol"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[symbol.key]}
    </svg>
  );
}

/** Readable labels for the Symbol select on the courses screen. */
export const SYMBOL_LABELS: Record<SymbolKey, { readonly en: string; readonly he: string }> = {
  medicine: { en: 'Medicine', he: 'רפואה' },
  economics: { en: 'Economics', he: 'כלכלה' },
  languages: { en: 'Languages', he: 'שפות' },
  education: { en: 'Education', he: 'חינוך' },
  sociology: { en: 'Social sciences', he: 'מדעי החברה' },
  law: { en: 'Law', he: 'משפטים' },
  history: { en: 'History', he: 'היסטוריה' },
  politics: { en: 'Politics', he: 'מדע המדינה' },
  'life-sciences': { en: 'Life sciences', he: 'מדעי החיים' },
  agriculture: { en: 'Agriculture', he: 'חקלאות' },
  psychology: { en: 'Psychology', he: 'פסיכולוגיה' },
  'area-studies': { en: 'Area studies', he: 'לימודי אזור' },
  'computer-science': { en: 'Computer science', he: 'מדעי המחשב' },
  chemistry: { en: 'Chemistry', he: 'כימיה' },
  philosophy: { en: 'Philosophy', he: 'פילוסופיה' },
  mathematics: { en: 'Mathematics', he: 'מתמטיקה' },
  physics: { en: 'Physics', he: 'פיזיקה' },
  religion: { en: 'Religion', he: 'לימודי דת' },
  geography: { en: 'Geography', he: 'גאוגרפיה' },
  earth: { en: 'Earth sciences', he: 'מדעי כדור הארץ' },
  communication: { en: 'Communication', he: 'תקשורת' },
  arts: { en: 'Arts', he: 'אמנויות' },
  statistics: { en: 'Statistics', he: 'סטטיסטיקה' },
  calculus: { en: 'Calculus', he: 'חשבון אינפיניטסימלי' },
  'linear-algebra': { en: 'Linear algebra', he: 'אלגברה לינארית' },
  discrete: { en: 'Discrete mathematics', he: 'מתמטיקה דיסקרטית' },
  algebra: { en: 'Algebra', he: 'אלגברה' },
  geometry: { en: 'Geometry', he: 'גאומטריה' },
  'number-theory': { en: 'Number theory', he: 'תורת המספרים' },
  probability: { en: 'Probability', he: 'הסתברות' },
  letter: { en: 'First letter', he: 'אות ראשונה' },
};
